import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, ClipboardList, TrendingUp, FileWarning, Plus, ArrowLeft, Search,
  CheckCircle2, XCircle, Clock, Eye, Edit3, Send, Star, AlertOctagon, UserCheck,
  Calendar, ChevronDown, ChevronRight, Shield, Target, BarChart3, Link2, ExternalLink,
  Phone, Mail, ContactRound, ListTodo
} from 'lucide-react';
import { NEU } from '../neuStyles';

const API = window.zignagenixAPI;

// ── Severity config ──────────────────────────────────────────────────────────
const SEVERITY_CONFIG = {
  verbal_warning: { label: 'Verbal Warning', color: '#f59e0b', bg: '#fef3c7' },
  written_warning: { label: 'Written Warning', color: '#f97316', bg: '#ffedd5' },
  final_warning: { label: 'Final Warning', color: '#ef4444', bg: '#fee2e2' },
  suspension: { label: 'Suspension', color: '#dc2626', bg: '#fecaca' },
  probation: { label: 'Probation', color: '#9333ea', bg: '#f3e8ff' },
  termination: { label: 'Termination', color: '#7f1d1d', bg: '#fecaca' },
  other: { label: 'Other', color: '#6b7280', bg: '#f3f4f6' },
};

const CATEGORY_OPTIONS = [
  { value: 'attendance', label: 'Attendance' },
  { value: 'conduct', label: 'Conduct' },
  { value: 'performance', label: 'Performance' },
  { value: 'policy_violation', label: 'Policy Violation' },
  { value: 'insubordination', label: 'Insubordination' },
  { value: 'safety', label: 'Safety' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'substance_abuse', label: 'Substance Abuse' },
  { value: 'confidentiality', label: 'Confidentiality' },
  { value: 'other', label: 'Other' },
];

const EVAL_STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#6b7280', icon: Edit3 },
  pending_self_review: { label: 'Self-Review', color: '#8b5cf6', icon: UserCheck },
  pending_review: { label: 'Pending Review', color: '#f59e0b', icon: Clock },
  pending_acknowledgment: { label: 'Pending Ack.', color: '#3b82f6', icon: Send },
  completed: { label: 'Completed', color: '#10b981', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: '#ef4444', icon: XCircle },
};

const DISC_STATUS_CONFIG = {
  active: { label: 'Active', color: '#ef4444' },
  resolved: { label: 'Resolved', color: '#10b981' },
  appealed: { label: 'Appealed', color: '#f59e0b' },
  overturned: { label: 'Overturned', color: '#6b7280' },
  expired: { label: 'Expired', color: '#9ca3af' },
};

const RATING_LABELS = {
  1: 'Unsatisfactory', 2: 'Needs Improvement', 3: 'Meets Expectations',
  4: 'Exceeds Expectations', 5: 'Outstanding'
};

// ── Badge component ──────────────────────────────────────────────────────────
function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, color: color, background: bg || `${color}18`,
      whiteSpace: 'nowrap'
    }}>{label}</span>
  );
}

// ── Star rating ──────────────────────────────────────────────────────────────
function StarRating({ value, onChange, readonly }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} size={20}
          fill={n <= value ? '#f59e0b' : 'none'}
          color={n <= value ? '#f59e0b' : '#d1d5db'}
          style={{ cursor: readonly ? 'default' : 'pointer' }}
          onClick={() => !readonly && onChange && onChange(n)}
        />
      ))}
      {value > 0 && <span style={{ marginLeft: 8, fontSize: 13, color: '#6b7280' }}>{RATING_LABELS[value]}</span>}
    </div>
  );
}

// ── CRM Link Panel (shows CRM info for an employee) ─────────────────────────
function CrmLinkPanel({ employeeId }) {
  const [crmData, setCrmData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    API.getPersonnelCrmLink(employeeId).then(data => {
      setCrmData(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div style={{ padding: 12, fontSize: 13, color: '#9ca3af' }}>Checking CRM link...</div>;
  if (!crmData?.linked) return (
    <div style={{ padding: 14, background: '#fefce8', borderRadius: 10, border: 'none', fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8, boxShadow: NEU.shadow.sm }}>
      <Link2 size={14} /> No CRM contact linked to this employee
    </div>
  );

  const c = crmData.contact;
  return (
    <div style={{ ...NEU.card, background: '#f0f9ff', padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ContactRound size={16} color="#0284c7" />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0284c7' }}>CRM Contact Linked</span>
        <Badge label={c.pipeline_stage?.replace(/_/g, ' ') || 'N/A'} color="#0284c7" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', fontSize: 13, marginBottom: 12 }}>
        <div><span style={{ color: '#6b7280' }}>Name:</span> {c.first_name} {c.last_name}</div>
        <div><span style={{ color: '#6b7280' }}>Type:</span> {c.contact_type?.replace(/_/g, ' ')}</div>
        {c.organization && <div><span style={{ color: '#6b7280' }}>Org:</span> {c.organization}</div>}
        {c.email && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={12} color="#6b7280" /> {c.email}</div>}
        {c.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={12} color="#6b7280" /> {c.phone}</div>}
      </div>

      {/* Recent CRM interactions */}
      {crmData.recentInteractions?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Recent CRM Interactions</div>
          {crmData.recentInteractions.map((int, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: '#374151' }}>
              <span>{int.subject?.substring(0, 50)}</span>
              <span style={{ color: '#9ca3af' }}>{int.interaction_date ? new Date(int.interaction_date).toLocaleDateString() : ''}</span>
            </div>
          ))}
        </div>
      )}

      {/* Open CRM tasks */}
      {crmData.openTasks?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <ListTodo size={12} /> Open CRM Tasks
          </div>
          {crmData.openTasks.map((t, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: '#374151' }}>{t.title?.substring(0, 45)}</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Badge label={t.priority} color={t.priority === 'urgent' ? '#dc2626' : t.priority === 'high' ? '#f59e0b' : '#6b7280'} />
                {t.due_date && <span style={{ color: '#9ca3af', fontSize: 11 }}>{new Date(t.due_date).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PA Timeline entries in CRM */}
      {crmData.paTimelineEntries?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Personnel Action History in CRM</div>
          {crmData.paTimelineEntries.map((e, i) => (
            <div key={i} style={{ fontSize: 12, padding: '3px 0', color: '#374151' }}>
              <span style={{ fontWeight: 500 }}>{e.title}</span>
              <span style={{ color: '#9ca3af', marginLeft: 8 }}>{new Date(e.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ──────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export default function PersonnelActionsPage({ user }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [view, setView] = useState('list'); // list | create | detail
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Disciplinary state
  const [discActions, setDiscActions] = useState([]);
  const [discDetail, setDiscDetail] = useState(null);
  const [discFilters, setDiscFilters] = useState({});

  // Evaluation state
  const [evals, setEvals] = useState([]);
  const [evalDetail, setEvalDetail] = useState(null);
  const [evalFilters, setEvalFilters] = useState({});
  const [evalTemplates, setEvalTemplates] = useState([]);

  // PIP state
  const [pips, setPips] = useState([]);
  const [pipDetail, setPipDetail] = useState(null);

  // Form state
  const [formData, setFormData] = useState({});

  // CRM integration state
  const [crmSummary, setCrmSummary] = useState([]);

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // ── Load data ────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const s = await API.getPersonnelActionStats();
      if (!s?.error) setStats(s);
    } catch (e) { console.error('Stats load failed:', e); }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const e = await API.getPersonnelEmployees();
      if (Array.isArray(e)) setEmployees(e);
    } catch (e) { console.error('Employees load failed:', e); }
  }, []);

  const loadDisciplinary = useCallback(async () => {
    setLoading(true);
    try {
      const d = await API.getDisciplinaryActions(discFilters);
      if (Array.isArray(d)) setDiscActions(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [discFilters]);

  const loadEvals = useCallback(async () => {
    setLoading(true);
    try {
      const e = await API.getEvaluations(evalFilters);
      if (Array.isArray(e)) setEvals(e);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [evalFilters]);

  const loadPips = useCallback(async () => {
    setLoading(true);
    try {
      const p = await API.getPIPs({});
      if (Array.isArray(p)) setPips(p);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadStats(); loadEmployees();
    API.getPersonnelCrmSummary().then(s => { if (Array.isArray(s)) setCrmSummary(s); }).catch(() => {});
  }, [loadStats, loadEmployees]);
  useEffect(() => { if (activeTab === 'disciplinary') loadDisciplinary(); }, [activeTab, loadDisciplinary]);
  useEffect(() => { if (activeTab === 'evaluations') { loadEvals(); API.getEvalTemplates().then(t => { if (Array.isArray(t)) setEvalTemplates(t); }); } }, [activeTab, loadEvals]);
  useEffect(() => { if (activeTab === 'pip') loadPips(); }, [activeTab, loadPips]);

  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const resetForm = () => { setFormData({}); setView('list'); };

  // ── Overview Tab ─────────────────────────────────────────────────────────
  function OverviewTab() {
    if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading statistics...</div>;

    const cards = [
      { label: 'Active Disciplinary', value: stats.disciplinary?.active_disciplinary || 0, color: '#ef4444', icon: AlertOctagon },
      { label: 'Appeals Pending', value: stats.disciplinary?.appealed || 0, color: '#f59e0b', icon: Shield },
      { label: 'Overdue Follow-ups', value: stats.disciplinary?.overdue_followups || 0, color: '#dc2626', icon: AlertTriangle },
      { label: 'Draft Evaluations', value: stats.evaluations?.draft_evals || 0, color: '#6b7280', icon: Edit3 },
      { label: 'Pending Review', value: stats.evaluations?.pending_review || 0, color: '#f59e0b', icon: Clock },
      { label: 'Completed This Year', value: stats.evaluations?.completed_this_year || 0, color: '#10b981', icon: CheckCircle2 },
      { label: 'Active PIPs', value: stats.pips?.active_pips || 0, color: '#9333ea', icon: Target },
      { label: 'Overdue PIPs', value: stats.pips?.overdue_pips || 0, color: '#dc2626', icon: FileWarning },
    ];

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} style={{
                ...NEU.card, padding: 20, borderRadius: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <Icon size={18} color={c.color} />
                  <span style={{ fontSize: 13, color: '#6b7280' }}>{c.label}</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{c.value}</div>
              </div>
            );
          })}
        </div>

        {stats.pendingEvals?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Upcoming Evaluation Deadlines</h3>
            <div style={{ ...NEU.card, overflow: 'hidden' }}>
              {stats.pendingEvals.map((e, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: 'none', boxShadow: i < stats.pendingEvals.length - 1 ? NEU.shadow.xs : 'none' }}>
                  <div>
                    <span style={{ fontWeight: 500 }}>{e.employee_name}</span>
                    <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>{e.evaluation_type}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Badge label={EVAL_STATUS_CONFIG[e.status]?.label || e.status} color={EVAL_STATUS_CONFIG[e.status]?.color || '#6b7280'} />
                    {e.due_date && <span style={{ fontSize: 12, color: '#6b7280' }}>Due: {new Date(e.due_date).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats.upcomingDeadlines?.length > 0 && (
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Upcoming Action Deadlines</h3>
            <div style={{ ...NEU.card, overflow: 'hidden' }}>
              {stats.upcomingDeadlines.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: 'none', boxShadow: i < stats.upcomingDeadlines.length - 1 ? NEU.shadow.xs : 'none' }}>
                  <div>
                    <span style={{ fontWeight: 500 }}>{d.employee_name}</span>
                    <Badge label={d.type === 'pip' ? 'PIP' : SEVERITY_CONFIG[d.severity]?.label || d.severity}
                      color={d.type === 'pip' ? '#9333ea' : SEVERITY_CONFIG[d.severity]?.color || '#6b7280'}
                      bg={d.type === 'pip' ? '#f3e8ff' : SEVERITY_CONFIG[d.severity]?.bg} />
                  </div>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>Deadline: {new Date(d.deadline).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CRM Integration Summary */}
        {crmSummary.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ContactRound size={18} color="#2E8B98" /> CRM Integration Status
            </h3>
            <div style={{ ...NEU.card, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 0, padding: '10px 16px', background: 'transparent', fontWeight: 600, fontSize: 12, color: '#6b7280', borderBottom: 'none', boxShadow: NEU.shadow.insetSm }}>
                <div>Employee</div>
                <div>Disciplinary</div>
                <div>Evaluations</div>
                <div>PIPs</div>
                <div>CRM Linked</div>
                <div>CRM Tasks</div>
              </div>
              {crmSummary.map((emp, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 0, padding: '10px 16px', borderBottom: 'none', boxShadow: i < crmSummary.length - 1 ? NEU.shadow.xs : 'none', fontSize: 13, alignItems: 'center' }}>
                  <div style={{ fontWeight: 500 }}>{emp.display_name}</div>
                  <div>{emp.active_disciplinary > 0 ? <Badge label={`${emp.active_disciplinary} active`} color="#ef4444" /> : <span style={{ color: '#d1d5db' }}>—</span>}</div>
                  <div>{emp.pending_evals > 0 ? <Badge label={`${emp.pending_evals} pending`} color="#f59e0b" /> : <span style={{ color: '#d1d5db' }}>—</span>}</div>
                  <div>{emp.active_pips > 0 ? <Badge label={`${emp.active_pips} active`} color="#9333ea" /> : <span style={{ color: '#d1d5db' }}>—</span>}</div>
                  <div>{emp.crm_linked ? <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}><Link2 size={14} /> Linked</span> : <span style={{ color: '#f59e0b' }}>Not linked</span>}</div>
                  <div>{emp.crm_open_tasks > 0 ? <Badge label={`${emp.crm_open_tasks} open`} color="#3b82f6" /> : <span style={{ color: '#d1d5db' }}>—</span>}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Disciplinary Tab ─────────────────────────────────────────────────────
  function DisciplinaryTab() {
    if (view === 'create') return <DisciplinaryForm />;
    if (view === 'detail' && discDetail) return <DisciplinaryDetail />;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={discFilters.severity || ''} onChange={e => setDiscFilters(p => ({ ...p, severity: e.target.value || undefined }))}
              style={filterSelectStyle}>
              <option value="">All Severities</option>
              {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={discFilters.status || ''} onChange={e => setDiscFilters(p => ({ ...p, status: e.target.value || undefined }))}
              style={filterSelectStyle}>
              <option value="">All Statuses</option>
              {Object.entries(DISC_STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <button onClick={() => { setFormData({ actionDate: new Date().toISOString().split('T')[0] }); setView('create'); }}
            style={primaryBtnStyle}><Plus size={16} /> New Disciplinary Action</button>
        </div>

        {loading ? <div style={loadingStyle}>Loading...</div> : discActions.length === 0 ? (
          <div style={emptyStyle}>No disciplinary actions found</div>
        ) : (
          <div style={{ ...NEU.card, overflow: 'hidden' }}>
            {discActions.map((d, i) => (
              <div key={d.id} onClick={async () => {
                const detail = await API.getDisciplinaryAction(d.id);
                if (!detail?.error) { setDiscDetail(detail); setView('detail'); }
              }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px',
                  borderBottom: 'none', boxShadow: i < discActions.length - 1 ? NEU.shadow.xs : 'none', cursor: 'pointer',
                  transition: 'background 0.15s', ':hover': { background: '#f9fafb' } }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{d.employee_name}</span>
                    <Badge label={SEVERITY_CONFIG[d.severity]?.label} color={SEVERITY_CONFIG[d.severity]?.color} bg={SEVERITY_CONFIG[d.severity]?.bg} />
                    <Badge label={DISC_STATUS_CONFIG[d.status]?.label} color={DISC_STATUS_CONFIG[d.status]?.color} />
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {CATEGORY_OPTIONS.find(c => c.value === d.category)?.label} — {d.description?.substring(0, 80)}{d.description?.length > 80 ? '...' : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>
                  <div>{new Date(d.action_date).toLocaleDateString()}</div>
                  <div style={{ marginTop: 2 }}>By: {d.issued_by_name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Disciplinary Create Form ─────────────────────────────────────────────
  function DisciplinaryForm() {
    const handleSubmit = async () => {
      setLoading(true);
      const result = await API.createDisciplinaryAction(formData);
      setLoading(false);
      if (result?.success) { showMsg('Disciplinary action created'); resetForm(); loadDisciplinary(); loadStats(); }
      else showMsg(result?.message || 'Failed to create', 'error');
    };

    return (
      <div>
        <button onClick={resetForm} style={backBtnStyle}><ArrowLeft size={16} /> Back</button>
        <h3 style={formTitleStyle}>New Disciplinary Action</h3>

        <div style={formGridStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Employee *</label>
            <select value={formData.employeeId || ''} onChange={e => handleField('employeeId', parseInt(e.target.value))} style={inputStyle}>
              <option value="">Select employee...</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.display_name} ({e.role})</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Severity *</label>
            <select value={formData.severity || ''} onChange={e => handleField('severity', e.target.value)} style={inputStyle}>
              <option value="">Select severity...</option>
              {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Category *</label>
            <select value={formData.category || ''} onChange={e => handleField('category', e.target.value)} style={inputStyle}>
              <option value="">Select category...</option>
              {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Action Date</label>
            <input type="date" value={formData.actionDate || ''} onChange={e => handleField('actionDate', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Incident Date</label>
            <input type="date" value={formData.incidentDate || ''} onChange={e => handleField('incidentDate', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Witness</label>
            <select value={formData.witnessId || ''} onChange={e => handleField('witnessId', e.target.value ? parseInt(e.target.value) : null)} style={inputStyle}>
              <option value="">None</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.display_name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ ...fieldStyle, marginTop: 16 }}>
          <label style={labelStyle}>Description *</label>
          <textarea value={formData.description || ''} onChange={e => handleField('description', e.target.value)}
            rows={4} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Describe the issue and context..." />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Incident Details</label>
          <textarea value={formData.incidentDetails || ''} onChange={e => handleField('incidentDetails', e.target.value)}
            rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Specific incident details..." />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Corrective Action Required</label>
          <textarea value={formData.correctiveAction || ''} onChange={e => handleField('correctiveAction', e.target.value)}
            rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Expected corrective actions and behavior changes..." />
        </div>

        <div style={formGridStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Improvement Deadline</label>
            <input type="date" value={formData.improvementDeadline || ''} onChange={e => handleField('improvementDeadline', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Follow-up Date</label>
            <input type="date" value={formData.followUpDate || ''} onChange={e => handleField('followUpDate', e.target.value)} style={inputStyle} />
          </div>
        </div>

        {(formData.severity === 'suspension') && (
          <div style={{ ...formGridStyle, marginTop: 16, padding: 16, ...NEU.inset }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Suspension Start</label>
              <input type="date" value={formData.suspensionStart || ''} onChange={e => handleField('suspensionStart', e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Suspension End</label>
              <input type="date" value={formData.suspensionEnd || ''} onChange={e => handleField('suspensionEnd', e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldStyle}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.suspensionWithPay || false} onChange={e => handleField('suspensionWithPay', e.target.checked)} />
                With Pay
              </label>
            </div>
          </div>
        )}

        {(formData.severity === 'probation') && (
          <div style={{ ...formGridStyle, marginTop: 16, padding: 16, ...NEU.inset }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Probation Start</label>
              <input type="date" value={formData.probationStart || ''} onChange={e => handleField('probationStart', e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Probation End</label>
              <input type="date" value={formData.probationEnd || ''} onChange={e => handleField('probationEnd', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Probation Conditions</label>
              <textarea value={formData.probationConditions || ''} onChange={e => handleField('probationConditions', e.target.value)}
                rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button onClick={handleSubmit} disabled={loading || !formData.employeeId || !formData.severity || !formData.category || !formData.description}
            style={{ ...primaryBtnStyle, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Saving...' : 'Create Disciplinary Action'}
          </button>
          <button onClick={resetForm} style={secondaryBtnStyle}>Cancel</button>
        </div>
      </div>
    );
  }

  // ── Disciplinary Detail View ─────────────────────────────────────────────
  function DisciplinaryDetail() {
    const d = discDetail;
    if (!d) return null;

    return (
      <div>
        <button onClick={() => { setDiscDetail(null); setView('list'); }} style={backBtnStyle}><ArrowLeft size={16} /> Back</button>

        {/* CRM Link Panel */}
        <CrmLinkPanel employeeId={d.employee_id} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{d.employee_name}</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge label={SEVERITY_CONFIG[d.severity]?.label} color={SEVERITY_CONFIG[d.severity]?.color} bg={SEVERITY_CONFIG[d.severity]?.bg} />
              <Badge label={DISC_STATUS_CONFIG[d.status]?.label} color={DISC_STATUS_CONFIG[d.status]?.color} />
              <Badge label={CATEGORY_OPTIONS.find(c => c.value === d.category)?.label || d.category} color="#6b7280" />
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13, color: '#6b7280' }}>
            <div>Issued: {new Date(d.action_date).toLocaleDateString()}</div>
            <div>By: {d.issued_by_name}</div>
            {d.witness_name && <div>Witness: {d.witness_name}</div>}
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Description</h4>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{d.description}</p>
        </div>

        {d.incident_details && (
          <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>Incident Details</h4>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{d.incident_details}</p>
            {d.incident_date && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>Incident Date: {new Date(d.incident_date).toLocaleDateString()}</p>}
          </div>
        )}

        {d.corrective_action && (
          <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>Corrective Action</h4>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{d.corrective_action}</p>
            {d.improvement_deadline && <p style={{ fontSize: 13, color: '#f59e0b', marginTop: 6 }}>Deadline: {new Date(d.improvement_deadline).toLocaleDateString()}</p>}
          </div>
        )}

        {/* Acknowledgment section */}
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Employee Acknowledgment</h4>
          {d.employee_acknowledged ? (
            <div style={{ color: '#10b981' }}>
              <CheckCircle2 size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
              Acknowledged on {new Date(d.employee_acknowledged_at).toLocaleDateString()}
              {d.employee_comments && <p style={{ marginTop: 8, fontStyle: 'italic', color: '#374151' }}>"{d.employee_comments}"</p>}
            </div>
          ) : d.employee_refused_to_sign ? (
            <div style={{ color: '#ef4444' }}>
              <XCircle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
              Employee refused to sign
              {d.employee_comments && <p style={{ marginTop: 8, fontStyle: 'italic', color: '#374151' }}>"{d.employee_comments}"</p>}
            </div>
          ) : (
            <span style={{ color: '#f59e0b' }}>Pending acknowledgment</span>
          )}
        </div>

        {/* Appeal section */}
        {d.appeal_filed && (
          <div style={{ ...sectionStyle, borderColor: '#f59e0b' }}>
            <h4 style={sectionTitleStyle}>Appeal</h4>
            <p>Filed: {d.appeal_date ? new Date(d.appeal_date).toLocaleDateString() : 'N/A'}</p>
            <p style={{ marginTop: 6 }}>Reason: {d.appeal_reason}</p>
            {d.appeal_decision && (
              <p style={{ marginTop: 8 }}>Decision: <strong style={{ textTransform: 'capitalize' }}>{d.appeal_decision}</strong></p>
            )}
          </div>
        )}

        {/* History */}
        {d.history?.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={sectionTitleStyle}>Action History</h4>
            <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 16, marginLeft: 8 }}>
              {d.history.map((h, i) => (
                <div key={i} style={{ marginBottom: 12, position: 'relative' }}>
                  <div style={{ position: 'absolute', left: -22, top: 4, width: 10, height: 10, borderRadius: '50%', background: '#2E8B98' }} />
                  <div style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: 500 }}>{h.changed_by_name}</span>
                    {h.old_status && h.new_status && <span style={{ color: '#6b7280' }}> changed status: {h.old_status} → {h.new_status}</span>}
                    {h.change_reason && <span style={{ color: '#6b7280' }}> — {h.change_reason}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(h.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Evaluations Tab ──────────────────────────────────────────────────────
  function EvaluationsTab() {
    if (view === 'create') return <EvaluationForm />;
    if (view === 'detail' && evalDetail) return <EvaluationDetail />;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select value={evalFilters.evaluationType || ''} onChange={e => setEvalFilters(p => ({ ...p, evaluationType: e.target.value || undefined }))}
              style={filterSelectStyle}>
              <option value="">All Types</option>
              <option value="annual">Annual</option>
              <option value="quarterly">Quarterly</option>
            </select>
            <select value={evalFilters.status || ''} onChange={e => setEvalFilters(p => ({ ...p, status: e.target.value || undefined }))}
              style={filterSelectStyle}>
              <option value="">All Statuses</option>
              {Object.entries(EVAL_STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <button onClick={() => {
            const now = new Date();
            setFormData({
              evaluationType: 'annual',
              periodStart: `${now.getFullYear()}-01-01`,
              periodEnd: `${now.getFullYear()}-12-31`,
              evaluationPeriod: `${now.getFullYear()}`,
              categoryScores: [],
              competencyRatings: [],
              priorGoalsReview: [],
              nextPeriodGoals: [],
            });
            setView('create');
          }} style={primaryBtnStyle}><Plus size={16} /> New Evaluation</button>
        </div>

        {loading ? <div style={loadingStyle}>Loading...</div> : evals.length === 0 ? (
          <div style={emptyStyle}>No evaluations found</div>
        ) : (
          <div style={{ ...NEU.card, overflow: 'hidden' }}>
            {evals.map((ev, i) => {
              const sc = EVAL_STATUS_CONFIG[ev.status] || {};
              return (
                <div key={ev.id} onClick={async () => {
                  const detail = await API.getEvaluation(ev.id);
                  if (!detail?.error) { setEvalDetail(detail); setView('detail'); }
                }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px',
                    borderBottom: 'none', boxShadow: i < evals.length - 1 ? NEU.shadow.xs : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{ev.employee_name}</span>
                      <Badge label={ev.evaluation_type === 'annual' ? 'Annual' : 'Quarterly'} color={ev.evaluation_type === 'annual' ? '#3b82f6' : '#8b5cf6'} />
                      <Badge label={sc.label || ev.status} color={sc.color || '#6b7280'} />
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      Period: {ev.evaluation_period || `${ev.period_start} — ${ev.period_end}`}
                      {ev.overall_rating > 0 && <span style={{ marginLeft: 12 }}>Rating: {'★'.repeat(ev.overall_rating)}{'☆'.repeat(5 - ev.overall_rating)}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>
                    <div>Evaluator: {ev.evaluator_name}</div>
                    {ev.due_date && <div>Due: {new Date(ev.due_date).toLocaleDateString()}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Evaluation Create Form ───────────────────────────────────────────────
  function EvaluationForm() {
    const handleSubmit = async () => {
      setLoading(true);
      const result = await API.createEvaluation(formData);
      setLoading(false);
      if (result?.success) { showMsg('Evaluation created'); resetForm(); loadEvals(); loadStats(); }
      else showMsg(result?.message || 'Failed to create', 'error');
    };

    const applyTemplate = (tId) => {
      const t = evalTemplates.find(t => t.id === parseInt(tId));
      if (!t) return;
      const cats = (typeof t.categories === 'string' ? JSON.parse(t.categories) : t.categories) || [];
      const comps = (typeof t.competencies === 'string' ? JSON.parse(t.competencies) : t.competencies) || [];
      setFormData(p => ({
        ...p,
        categoryScores: cats.map(c => ({ category: c.name, weight: c.weight, rating: 0, comments: '' })),
        competencyRatings: comps.map(c => ({ competency: c.name, rating: 0, comments: '' })),
      }));
    };

    const updatePeriodDates = (type) => {
      const now = new Date();
      const y = now.getFullYear();
      if (type === 'annual') {
        setFormData(p => ({ ...p, evaluationType: 'annual', periodStart: `${y}-01-01`, periodEnd: `${y}-12-31`, evaluationPeriod: `${y}` }));
      } else {
        const q = Math.ceil((now.getMonth() + 1) / 3);
        const qStart = new Date(y, (q - 1) * 3, 1);
        const qEnd = new Date(y, q * 3, 0);
        setFormData(p => ({
          ...p, evaluationType: 'quarterly',
          periodStart: qStart.toISOString().split('T')[0],
          periodEnd: qEnd.toISOString().split('T')[0],
          evaluationPeriod: `Q${q} ${y}`
        }));
      }
    };

    return (
      <div>
        <button onClick={resetForm} style={backBtnStyle}><ArrowLeft size={16} /> Back</button>
        <h3 style={formTitleStyle}>New Performance Evaluation</h3>

        <div style={formGridStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Employee *</label>
            <select value={formData.employeeId || ''} onChange={e => handleField('employeeId', parseInt(e.target.value))} style={inputStyle}>
              <option value="">Select employee...</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.display_name} ({e.role})</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Type *</label>
            <select value={formData.evaluationType || ''} onChange={e => { handleField('evaluationType', e.target.value); updatePeriodDates(e.target.value); }} style={inputStyle}>
              <option value="annual">Annual Review</option>
              <option value="quarterly">Quarterly Check-in</option>
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Period Start *</label>
            <input type="date" value={formData.periodStart || ''} onChange={e => handleField('periodStart', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Period End *</label>
            <input type="date" value={formData.periodEnd || ''} onChange={e => handleField('periodEnd', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Period Label</label>
            <input type="text" value={formData.evaluationPeriod || ''} onChange={e => handleField('evaluationPeriod', e.target.value)} style={inputStyle} placeholder="e.g. 2026, Q1 2026" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Due Date</label>
            <input type="date" value={formData.dueDate || ''} onChange={e => handleField('dueDate', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Secondary Reviewer</label>
            <select value={formData.secondaryReviewerId || ''} onChange={e => handleField('secondaryReviewerId', e.target.value ? parseInt(e.target.value) : null)} style={inputStyle}>
              <option value="">None (skip-level)</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.display_name}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Apply Template</label>
            <select onChange={e => applyTemplate(e.target.value)} style={inputStyle} defaultValue="">
              <option value="">Select template...</option>
              {evalTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ ...fieldStyle, marginTop: 16 }}>
          <label style={labelStyle}>Strengths</label>
          <textarea value={formData.strengths || ''} onChange={e => handleField('strengths', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Areas for Improvement</label>
          <textarea value={formData.areasForImprovement || ''} onChange={e => handleField('areasForImprovement', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Development Plan</label>
          <textarea value={formData.developmentPlan || ''} onChange={e => handleField('developmentPlan', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Evaluator Comments</label>
          <textarea value={formData.evaluatorComments || ''} onChange={e => handleField('evaluatorComments', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {/* Overall Rating */}
        <div style={{ ...fieldStyle, marginTop: 16 }}>
          <label style={labelStyle}>Overall Rating</label>
          <StarRating value={formData.overallRating || 0} onChange={v => {
            handleField('overallRating', v);
            handleField('ratingLabel', ['', 'unsatisfactory', 'needs_improvement', 'meets_expectations', 'exceeds_expectations', 'outstanding'][v]);
          }} />
        </div>

        {/* Compensation (annual only) */}
        {formData.evaluationType === 'annual' && (
          <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 10, border: 'none', boxShadow: NEU.shadow.xs }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#166534' }}>Compensation Recommendations</h4>
            <div style={formGridStyle}>
              <div style={fieldStyle}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.raiseRecommended || false} onChange={e => handleField('raiseRecommended', e.target.checked)} />
                  Recommend Raise
                </label>
                {formData.raiseRecommended && (
                  <input type="number" value={formData.raisePercentage || ''} onChange={e => handleField('raisePercentage', parseFloat(e.target.value))}
                    style={inputStyle} placeholder="% increase" step="0.5" min="0" max="100" />
                )}
              </div>
              <div style={fieldStyle}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.promotionRecommended || false} onChange={e => handleField('promotionRecommended', e.target.checked)} />
                  Recommend Promotion
                </label>
                {formData.promotionRecommended && (
                  <input type="text" value={formData.promotionToTitle || ''} onChange={e => handleField('promotionToTitle', e.target.value)}
                    style={inputStyle} placeholder="New title" />
                )}
              </div>
              <div style={fieldStyle}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.bonusRecommended || false} onChange={e => handleField('bonusRecommended', e.target.checked)} />
                  Recommend Bonus
                </label>
                {formData.bonusRecommended && (
                  <input type="number" value={formData.bonusAmount || ''} onChange={e => handleField('bonusAmount', parseFloat(e.target.value))}
                    style={inputStyle} placeholder="$ amount" step="100" min="0" />
                )}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button onClick={handleSubmit} disabled={loading || !formData.employeeId || !formData.periodStart || !formData.periodEnd}
            style={{ ...primaryBtnStyle, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Saving...' : 'Create Evaluation'}
          </button>
          <button onClick={resetForm} style={secondaryBtnStyle}>Cancel</button>
        </div>
      </div>
    );
  }

  // ── Evaluation Detail View ───────────────────────────────────────────────
  function EvaluationDetail() {
    const ev = evalDetail;
    if (!ev) return null;
    const sc = EVAL_STATUS_CONFIG[ev.status] || {};
    const cats = (typeof ev.category_scores === 'string' ? JSON.parse(ev.category_scores) : ev.category_scores) || [];
    const comps = (typeof ev.competency_ratings === 'string' ? JSON.parse(ev.competency_ratings) : ev.competency_ratings) || [];
    const priorGoals = (typeof ev.prior_goals_review === 'string' ? JSON.parse(ev.prior_goals_review) : ev.prior_goals_review) || [];
    const nextGoals = (typeof ev.next_period_goals === 'string' ? JSON.parse(ev.next_period_goals) : ev.next_period_goals) || [];

    return (
      <div>
        <button onClick={() => { setEvalDetail(null); setView('list'); }} style={backBtnStyle}><ArrowLeft size={16} /> Back</button>

        {/* CRM Link Panel */}
        <CrmLinkPanel employeeId={ev.employee_id} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{ev.employee_name}</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge label={ev.evaluation_type === 'annual' ? 'Annual Review' : 'Quarterly Check-in'} color={ev.evaluation_type === 'annual' ? '#3b82f6' : '#8b5cf6'} />
              <Badge label={sc.label} color={sc.color} />
              <span style={{ fontSize: 13, color: '#6b7280' }}>{ev.evaluation_period}</span>
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13, color: '#6b7280' }}>
            <div>Evaluator: {ev.evaluator_name}</div>
            {ev.due_date && <div>Due: {new Date(ev.due_date).toLocaleDateString()}</div>}
          </div>
        </div>

        {/* Overall Rating */}
        {ev.overall_rating > 0 && (
          <div style={{ ...sectionStyle, textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#6b7280' }}>Overall Rating</div>
            <StarRating value={ev.overall_rating} readonly />
          </div>
        )}

        {/* Category Scores */}
        {cats.length > 0 && (
          <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>Category Scores</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 50px', gap: '8px 16px', alignItems: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#9ca3af' }}>Category</div>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#9ca3af' }}>Weight</div>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#9ca3af' }}>Rating</div>
              {cats.map((c, i) => (
                <React.Fragment key={i}>
                  <div style={{ fontSize: 14 }}>{c.category || c.name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{c.weight}%</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#2E8B98' }}>{c.rating || '-'}/5</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Written sections */}
        {ev.strengths && <div style={sectionStyle}><h4 style={sectionTitleStyle}>Strengths</h4><p style={{ whiteSpace: 'pre-wrap' }}>{ev.strengths}</p></div>}
        {ev.areas_for_improvement && <div style={sectionStyle}><h4 style={sectionTitleStyle}>Areas for Improvement</h4><p style={{ whiteSpace: 'pre-wrap' }}>{ev.areas_for_improvement}</p></div>}
        {ev.development_plan && <div style={sectionStyle}><h4 style={sectionTitleStyle}>Development Plan</h4><p style={{ whiteSpace: 'pre-wrap' }}>{ev.development_plan}</p></div>}

        {/* Self evaluation */}
        {ev.self_submitted_at && (
          <div style={{ ...sectionStyle, borderColor: '#8b5cf6' }}>
            <h4 style={sectionTitleStyle}>Self-Evaluation</h4>
            {ev.self_rating && <div style={{ marginBottom: 8 }}><StarRating value={ev.self_rating} readonly /></div>}
            {ev.self_assessment && <p style={{ marginBottom: 6 }}><strong>Assessment:</strong> {ev.self_assessment}</p>}
            {ev.self_accomplishments && <p style={{ marginBottom: 6 }}><strong>Accomplishments:</strong> {ev.self_accomplishments}</p>}
            {ev.self_challenges && <p style={{ marginBottom: 6 }}><strong>Challenges:</strong> {ev.self_challenges}</p>}
          </div>
        )}

        {/* Acknowledgment */}
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Employee Acknowledgment</h4>
          {ev.employee_acknowledged ? (
            <div style={{ color: '#10b981' }}>
              <CheckCircle2 size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
              Acknowledged on {new Date(ev.employee_acknowledged_at).toLocaleDateString()}
              {ev.employee_disagrees && <span style={{ color: '#ef4444', marginLeft: 10 }}>(Employee Disagrees)</span>}
              {ev.employee_comments && <p style={{ marginTop: 8, fontStyle: 'italic', color: '#374151' }}>"{ev.employee_comments}"</p>}
            </div>
          ) : (
            <span style={{ color: '#f59e0b' }}>Pending acknowledgment</span>
          )}
        </div>

        {/* Compensation (annual) */}
        {ev.evaluation_type === 'annual' && (ev.raise_recommended || ev.promotion_recommended || ev.bonus_recommended) && (
          <div style={{ ...sectionStyle, borderColor: '#10b981', background: '#f0fdf4' }}>
            <h4 style={sectionTitleStyle}>Compensation Recommendations</h4>
            {ev.raise_recommended && <p>Raise: {ev.raise_percentage}%</p>}
            {ev.promotion_recommended && <p>Promotion to: {ev.promotion_to_title}</p>}
            {ev.bonus_recommended && <p>Bonus: ${ev.bonus_amount?.toLocaleString()}</p>}
          </div>
        )}

        {/* History */}
        {ev.history?.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={sectionTitleStyle}>Evaluation History</h4>
            <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 16, marginLeft: 8 }}>
              {ev.history.map((h, i) => (
                <div key={i} style={{ marginBottom: 12, position: 'relative' }}>
                  <div style={{ position: 'absolute', left: -22, top: 4, width: 10, height: 10, borderRadius: '50%', background: '#2E8B98' }} />
                  <div style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: 500 }}>{h.changed_by_name}</span>
                    {h.old_status && h.new_status && <span style={{ color: '#6b7280' }}> — {h.old_status} → {h.new_status}</span>}
                    {h.change_reason && <span style={{ color: '#6b7280' }}> — {h.change_reason}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(h.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── PIP Tab ──────────────────────────────────────────────────────────────
  function PIPTab() {
    if (view === 'create') return <PIPForm />;
    if (view === 'detail' && pipDetail) return <PIPDetail />;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div />
          <button onClick={() => { setFormData({ startDate: new Date().toISOString().split('T')[0] }); setView('create'); }}
            style={primaryBtnStyle}><Plus size={16} /> New PIP</button>
        </div>
        {loading ? <div style={loadingStyle}>Loading...</div> : pips.length === 0 ? (
          <div style={emptyStyle}>No performance improvement plans found</div>
        ) : (
          <div style={{ ...NEU.card, overflow: 'hidden' }}>
            {pips.map((p, i) => (
              <div key={p.id} onClick={async () => {
                const detail = await API.getPIP(p.id);
                if (!detail?.error) { setPipDetail(detail); setView('detail'); }
              }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px',
                  borderBottom: 'none', boxShadow: i < evals.length - 1 ? NEU.shadow.xs : 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{p.employee_name}</span>
                    <Badge label={p.status.replace(/_/g, ' ')} color={p.status === 'active' ? '#9333ea' : p.status.includes('success') ? '#10b981' : '#ef4444'} />
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {new Date(p.start_date).toLocaleDateString()} — {new Date(p.end_date).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Initiated by: {p.initiated_by_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── PIP Form ─────────────────────────────────────────────────────────────
  function PIPForm() {
    const handleSubmit = async () => {
      setLoading(true);
      const result = await API.createPIP(formData);
      setLoading(false);
      if (result?.success) { showMsg('PIP created'); resetForm(); loadPips(); loadStats(); }
      else showMsg(result?.message || 'Failed to create', 'error');
    };

    return (
      <div>
        <button onClick={resetForm} style={backBtnStyle}><ArrowLeft size={16} /> Back</button>
        <h3 style={formTitleStyle}>New Performance Improvement Plan</h3>

        <div style={formGridStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Employee *</label>
            <select value={formData.employeeId || ''} onChange={e => handleField('employeeId', parseInt(e.target.value))} style={inputStyle}>
              <option value="">Select employee...</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.display_name} ({e.role})</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Start Date *</label>
            <input type="date" value={formData.startDate || ''} onChange={e => handleField('startDate', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>End Date *</label>
            <input type="date" value={formData.endDate || ''} onChange={e => handleField('endDate', e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ ...fieldStyle, marginTop: 16 }}>
          <label style={labelStyle}>Support Provided</label>
          <textarea value={formData.supportProvided || ''} onChange={e => handleField('supportProvided', e.target.value)}
            rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Training, mentoring, resources..." />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Resources Offered</label>
          <textarea value={formData.resourcesOffered || ''} onChange={e => handleField('resourcesOffered', e.target.value)}
            rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Consequences if Not Met</label>
          <textarea value={formData.consequencesIfNotMet || ''} onChange={e => handleField('consequencesIfNotMet', e.target.value)}
            rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="e.g. Further disciplinary action up to and including termination" />
        </div>

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button onClick={handleSubmit} disabled={loading || !formData.employeeId || !formData.startDate || !formData.endDate}
            style={{ ...primaryBtnStyle, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Saving...' : 'Create PIP'}
          </button>
          <button onClick={resetForm} style={secondaryBtnStyle}>Cancel</button>
        </div>
      </div>
    );
  }

  // ── PIP Detail ───────────────────────────────────────────────────────────
  function PIPDetail() {
    const p = pipDetail;
    if (!p) return null;
    const defs = (typeof p.deficiencies === 'string' ? JSON.parse(p.deficiencies) : p.deficiencies) || [];
    const milestones = (typeof p.milestones === 'string' ? JSON.parse(p.milestones) : p.milestones) || [];
    const checkIns = (typeof p.check_ins === 'string' ? JSON.parse(p.check_ins) : p.check_ins) || [];

    return (
      <div>
        <button onClick={() => { setPipDetail(null); setView('list'); }} style={backBtnStyle}><ArrowLeft size={16} /> Back</button>

        {/* CRM Link Panel */}
        <CrmLinkPanel employeeId={p.employee_id} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{p.employee_name} — PIP</h3>
            <Badge label={p.status.replace(/_/g, ' ')} color={p.status === 'active' ? '#9333ea' : p.status.includes('success') ? '#10b981' : '#ef4444'} />
          </div>
          <div style={{ textAlign: 'right', fontSize: 13, color: '#6b7280' }}>
            <div>{new Date(p.start_date).toLocaleDateString()} — {new Date(p.end_date).toLocaleDateString()}</div>
            <div>Initiated by: {p.initiated_by_name}</div>
          </div>
        </div>

        {p.support_provided && <div style={sectionStyle}><h4 style={sectionTitleStyle}>Support Provided</h4><p>{p.support_provided}</p></div>}
        {p.consequences_if_not_met && <div style={sectionStyle}><h4 style={sectionTitleStyle}>Consequences</h4><p>{p.consequences_if_not_met}</p></div>}

        {defs.length > 0 && (
          <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>Deficiency Areas</h4>
            {defs.map((d, i) => (
              <div key={i} style={{ marginBottom: 12, padding: 10, ...NEU.cardSm }}>
                <div style={{ fontWeight: 600 }}>{d.area}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Current: {d.current_level} → Expected: {d.expected_level}</div>
              </div>
            ))}
          </div>
        )}

        {milestones.length > 0 && (
          <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>Milestones</h4>
            {milestones.map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                {m.completed ? <CheckCircle2 size={16} color="#10b981" /> : <Clock size={16} color="#f59e0b" />}
                <span>{m.milestone}</span>
                {m.due_date && <span style={{ fontSize: 12, color: '#6b7280' }}>Due: {m.due_date}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Acknowledgment */}
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Employee Acknowledgment</h4>
          {p.employee_acknowledged ? (
            <div style={{ color: '#10b981' }}>
              <CheckCircle2 size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
              Acknowledged on {new Date(p.employee_acknowledged_at).toLocaleDateString()}
            </div>
          ) : (
            <span style={{ color: '#f59e0b' }}>Pending acknowledgment</span>
          )}
        </div>
      </div>
    );
  }

  // ── CRM Integration Tab ───────────────────────────────────────────────────
  function CrmTab() {
    const [selectedEmpId, setSelectedEmpId] = useState(null);

    return (
      <div>
        <div style={{ marginBottom: 20, padding: 16, background: '#f0f9ff', borderRadius: 10, border: 'none', boxShadow: NEU.shadow.sm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <ContactRound size={18} color="#0284c7" />
            <span style={{ fontSize: 15, fontWeight: 600, color: '#0284c7' }}>CRM Integration</span>
          </div>
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0 }}>
            Personnel actions are automatically synced to the CRM system. When disciplinary actions, evaluations, or PIPs are created,
            they are logged as CRM interactions, timeline notes, and tasks linked to the employee's CRM contact record.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          {/* Employee selector */}
          <div style={{ width: 280, flexShrink: 0 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Select Employee</h4>
            <div style={{ ...NEU.card, overflow: 'hidden', maxHeight: 500, overflowY: 'auto' }}>
              {employees.map(emp => (
                <div key={emp.id} onClick={() => setSelectedEmpId(emp.id)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer', borderBottom: 'none', boxShadow: NEU.shadow.xs,
                    background: selectedEmpId === emp.id ? '#f0f9ff' : '',
                    borderLeft: selectedEmpId === emp.id ? '3px solid #2E8B98' : '3px solid transparent',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { if (selectedEmpId !== emp.id) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (selectedEmpId !== emp.id) e.currentTarget.style.background = ''; }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{emp.display_name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'capitalize' }}>{emp.role}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CRM detail panel */}
          <div style={{ flex: 1 }}>
            {selectedEmpId ? (
              <CrmLinkPanel employeeId={selectedEmpId} />
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#F8F9FA', borderRadius: 10, boxShadow: NEU.shadow.sm }}>
                Select an employee to view their CRM link status
              </div>
            )}
          </div>
        </div>

        {/* CRM Sync Summary Table */}
        {crmSummary.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Active Personnel Actions — CRM Sync Status</h4>
            <div style={{ ...NEU.card, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 0, padding: '10px 16px', background: 'transparent', fontWeight: 600, fontSize: 12, color: '#6b7280', borderBottom: 'none', boxShadow: NEU.shadow.insetSm }}>
                <div>Employee</div>
                <div>Disciplinary</div>
                <div>Evaluations</div>
                <div>PIPs</div>
                <div>CRM Status</div>
                <div>CRM Tasks</div>
              </div>
              {crmSummary.map((emp, i) => (
                <div key={i} onClick={() => setSelectedEmpId(emp.employee_id)}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: 0, padding: '10px 16px', borderBottom: 'none', boxShadow: i < evals.length - 1 ? NEU.shadow.xs : 'none', fontSize: 13, alignItems: 'center', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <div style={{ fontWeight: 500 }}>{emp.display_name}</div>
                  <div>{emp.active_disciplinary > 0 ? <Badge label={`${emp.active_disciplinary} active`} color="#ef4444" /> : '—'}</div>
                  <div>{emp.pending_evals > 0 ? <Badge label={`${emp.pending_evals} pending`} color="#f59e0b" /> : '—'}</div>
                  <div>{emp.active_pips > 0 ? <Badge label={`${emp.active_pips} active`} color="#9333ea" /> : '—'}</div>
                  <div>{emp.crm_linked ?
                    <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}><Link2 size={14} /> Linked</span> :
                    <span style={{ color: '#f59e0b' }}>Not linked</span>}
                  </div>
                  <div>{emp.crm_open_tasks > 0 ? <Badge label={`${emp.crm_open_tasks} open`} color="#3b82f6" /> : '—'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── MAIN RENDER ─────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'disciplinary', label: 'Disciplinary', icon: AlertOctagon },
    { id: 'evaluations', label: 'Evaluations', icon: ClipboardList },
    { id: 'pip', label: 'PIPs', icon: Target },
    { id: 'crm', label: 'CRM Link', icon: ContactRound },
  ];

  return (
    <div style={{ padding: 32, maxWidth: 1100 }}>
      {message && (
        <div style={{
          padding: '12px 20px', borderRadius: 10, marginBottom: 16,
          background: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color: message.type === 'error' ? '#dc2626' : '#166534',
          border: 'none',
          boxShadow: NEU.shadow.xs
        }}>{message.text}</div>
      )}

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Personnel Actions</h1>
        <p style={{ color: '#6b7280', fontSize: 14 }}>Disciplinary actions, performance evaluations, and improvement plans</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
        {tabs.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => { setActiveTab(t.id); setView('list'); setDiscDetail(null); setEvalDetail(null); setPipDetail(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                border: 'none', borderBottom: active ? '2px solid #2E8B98' : '2px solid transparent',
                background: 'none', color: active ? '#2E8B98' : '#6b7280', fontWeight: active ? 600 : 400,
                fontSize: 14, cursor: 'pointer', marginBottom: -2, transition: 'all 0.15s'
              }}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'disciplinary' && <DisciplinaryTab />}
      {activeTab === 'evaluations' && <EvaluationsTab />}
      {activeTab === 'pip' && <PIPTab />}
      {activeTab === 'crm' && <CrmTab />}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────
const primaryBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
  ...NEU.btnPrimary, fontSize: 14, transition: 'all 0.2s'
};
const secondaryBtnStyle = {
  padding: '10px 20px', background: '#F8F9FA', color: '#374151',
  border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 500, fontSize: 14, boxShadow: NEU.shadow.xs
};
const backBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', marginBottom: 16,
  background: '#F8F9FA', border: 'none', borderRadius: 8, color: '#6b7280',
  cursor: 'pointer', fontSize: 13, boxShadow: NEU.shadow.xs
};
const filterSelectStyle = {
  padding: '8px 12px', borderRadius: 8, border: 'none', fontSize: 13, color: '#374151',
  background: '#F8F9FA', cursor: 'pointer', boxShadow: NEU.shadow.insetSm
};
const formGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 };
const labelStyle = { fontSize: 13, fontWeight: 500, color: '#374151' };
const inputStyle = {
  padding: '10px 14px', borderRadius: 8, border: 'none', fontSize: 14, color: '#111827',
  background: '#F8F9FA', outline: 'none', transition: 'border 0.15s', width: '100%', boxSizing: 'border-box', boxShadow: NEU.shadow.insetSm
};
const formTitleStyle = { fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#111827' };
const sectionStyle = {
  background: '#F8F9FA', borderRadius: 10, padding: 20, marginBottom: 16,
  border: 'none', boxShadow: NEU.shadow.sm
};
const sectionTitleStyle = { fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 10 };
const loadingStyle = { padding: 40, textAlign: 'center', color: '#9ca3af' };
const emptyStyle = { padding: 40, textAlign: 'center', color: '#9ca3af', background: '#F8F9FA', borderRadius: 10, boxShadow: NEU.shadow.sm };
